import { createSelector } from "reselect"
import { get, chain, map, filter, first, mergeWith, size, some } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups } from "helpers/rollups"
import { computeFilteredGroupedContributors, computeSelectedGroupSiblings, computeSelectedGroupValue, computeSortedContributors, computeSortedGroupContributors, positionSetContributorsSummaryText } from "helpers/contributors"
import { getViewType, getPortfolioName, getShowNotHeld, getMustAggregateFactors, getStyleExposureAggregation } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { getSelectedViewsFactors } from "selectors/factors"
import { getTagCategories, getAttributeCategories } from "modules/portfolio-root/selectors"
import { getScreenName } from "modules/analyze-factor-sensitivity/selectors/file"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { aggregateExposuresFactors } from "helpers/factorClassification"

const EMPTY_OBJECT = {}

function getFactors(factors, mustAggregateFactors, styleAggregation, selectedViewFactors, factorsClassification) {
  if (!mustAggregateFactors) {
    return factors
  }

  let aggregatedFactors = aggregateExposuresFactors(factorsClassification, factors, styleAggregation, "sensitivity")
  if (size(selectedViewFactors)) {
    aggregatedFactors = chain(factors)
      .filter(factor => some(selectedViewFactors, item => item.id === factor.id))
      .concat(aggregatedFactors)
      .value()
  }
  return aggregatedFactors
}

function computeNotHeldBucket(contributors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification) {
  const merge = (obj, source) => mergeWith(obj, source, (objValue, srcValue) =>
    !objValue ? srcValue : objValue + srcValue,
  )

  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.percentEquity
      const factors = getFactors(value.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification)
      merge(acc.factors, chain(factors)
        .keyBy("id")
        .mapValues(f => f.sensitivity * 100)
        .value())

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      factors: {},
    })
    .value()
}

function mapFactorSensitivityContributors(data, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification) {
  const result = chain(data)
    .filter(item => showNotHeld || !item.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
      const factors = getFactors(security.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification)
      return {
        id: security.id,
        country: security.country,
        description: security.description,
        classification: get(security, "classification.name"),
        currency: security.currency,
        assetClass: security.assetClass,
        assetSubclass: security.assetSubclass,
        notHeld: security.notHeld,
        size: security.percentEquity,
        bloombergTicker: security.bloombergTicker,
        modelProviderId: security.modelProviderId,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => f.sensitivity * 100)
          .value(),
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(data, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification))
  }
  return result
}

export const getGroupBy = (state, props) => get(props, "contributorsGrouping.id")

const getContributorsData = state => get(state, "global.data.factorSensitivityContributors.data.model.simulation.factorSensitivityContributors")
const getGroupedContributorsData = state => get(state, "global.data.groupedFactorSensitivityContributors.data.model.simulation.groupedFactorSensitivityContributors")
const getSelected = state => get(state, "modules.analyzeFactorSensitivityDetails.selected", EMPTY_OBJECT)
const getContributorsGrouping = (state, props) => get(props, "contributorsGrouping")
const getRawSelectedGroup = (state, props) => get(props, "selectedGroup", EMPTY_OBJECT)
const getOrder = state => get(state, "modules.analyzeFactorSensitivityDetails.order", EMPTY_OBJECT)
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getSelectedDate = (state, props) => props.selectedDate

export const getSelectedGroup = createSelector(
  [getRawSelectedGroup, getGroupedContributorsData],
  computeSelectedGroup,
)

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

const getSelectedField = createSelector(
  [getSelected],
  (selected) => get(selected, "field", "total"),
)

const getContributors = createSelector(
  [getContributorsData, getShowNotHeld, getMustAggregateFactors, getStyleExposureAggregation, getSelectedViewsFactors, getModelFactorsClassification],
  mapFactorSensitivityContributors,
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) => filter(data, security => get(security, selectedField)),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  computeSortedContributors,
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld, getMustAggregateFactors, getStyleExposureAggregation, getSelectedViewsFactors, getModelFactorsClassification],
  (data, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification) => map(data, group => {
    const factors = getFactors(group.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification)
    return {
      ...group,
      factors: chain(factors)
        .keyBy("id")
        .mapValues(f => f.sensitivity * 100)
        .value(),
      contributors: mapFactorSensitivityContributors(group.contributors, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification),
    }
  }),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  computeFilteredGroupedContributors,
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  computeSortedGroupContributors,
)

const getAccountRollups = createSelector(
  [getTagCategories, getClassifications, getAttributeCategories],
  (tagCategories, classifications, attributeCategories) => ({
    tagCategories,
    classifications,
    attributeCategories,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType, getShowNotHeld],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getContributorsGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelected, getContributorsGrouping, getPortfolioName, getSelectedDate, getViewType, getSelectedGroup, getScreenName],
  (data, groupedData, selected, contributorsGrouping, portfolioName, selectedDate, viewType, selectedGroup, screenName) => {
    const defaultOptions = {
      type: screenName,
      portfolioName,
      selected,
      selectedDate,
      viewType,
      net: true,
    }

    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        ...defaultOptions,
        group,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        ...defaultOptions,
        contributorsGrouping,
        data: groupedData,
      })
    }

    return computeContributorsCSV({
      ...defaultOptions,
      data,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  computeSelectedGroupValue,
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  computeSelectedGroupSiblings,
)
